<template>
  <div class="page">
    <div class="title-row">
      <div>
        <div style="font-size: 20px; font-weight: bold">{{ $t('home.publishSystemMessage') }}</div>
        <p>{{ $t('home.publishSystemMessageDesc') }}</p>
      </div>
      <el-button @click="handleBackList">{{ $t('home.backToMessageList') }}</el-button>
    </div>

    <el-form
      ref="formRef"
      :disabled="viewMode === 'view'"
      :model="form"
      :rules="rules"
      label-width="auto"
    >
      <!-- ========== 一、公告基础信息 ========== -->
      <el-card class="panel" shadow="never">
        <template slot="header">
          <h2 class="panel-title">{{ $t('home.basicInfoSection') }}</h2>
        </template>
        <el-row :gutter="16">
          <el-col :span="24">
            <el-form-item :label="$t('home.messageType')" prop="msgType">
              <el-select v-model="form.msgType" style="width: 100%">
                <el-option :label="$t('home.urgentAnnouncement')" value="URGENT_NOTICE" />
                <el-option :label="$t('home.operationNotice')" value="OPERATIONAL_NOTICE" />
                <el-option :label="$t('home.systemUpdate')" value="GXTS" />
                <el-option :label="$t('home.normalNotice')" value="TZ" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('home.titleChinese')" prop="zhTitle">
              <el-input
                v-model="form.zhTitle"
                :maxlength="100"
                :placeholder="$t('home.pleaseInput')"
                show-word-limit
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('home.titlePortuguese')" prop="title">
              <el-input
                v-model="form.title"
                :maxlength="100"
                :placeholder="$t('home.pleaseInput')"
                show-word-limit
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('home.abstractChinese')">
              <el-input
                v-model="form.zhAbstract"
                :maxlength="200"
                :placeholder="$t('home.pleaseInput')"
                show-word-limit
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('home.abstractPortuguese')">
              <el-input
                v-model="form.puAbstract"
                :maxlength="200"
                :placeholder="$t('home.pleaseInput')"
                show-word-limit
              />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('home.contentChinese')">
              <tinymce
                v-model="form.zhText"
                :height="150"
                :is-image="isImage"
                :msg-plugin="msgPlugin"
                :placeholder="$t('GlobalSetObj.pleaseInputContent') + '...'"
                :toolbar="toolbar"
              />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('home.contentPortuguese')">
              <tinymce
                v-model="form.puText"
                :height="150"
                :is-image="isImage"
                :msg-plugin="msgPlugin"
                :placeholder="$t('GlobalSetObj.pleaseInputContent') + '...'"
                :toolbar="toolbar"
              />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('home.image')">
              <div class="upload-area">
                <div class="upload-info">
                  <div class="upload-icon">🖼️</div>
                  <div class="upload-text">
                    <strong>{{ $t('home.uploadMessageImage') }}</strong>
                    <p>{{ $t('home.uploadImageDesc') }}</p>
                    <el-upload
                      :accept="acceptType"
                      :action="uploadAction"
                      :before-upload="beforeUpload"
                      :http-request="customUpload"
                      :show-file-list="false"
                      class="upload-btn"
                    >
                      <el-button :loading="uploadLoading" size="small">
                        {{ imageUrl ? $t('home.changeImage') : $t('home.selectImage') }}
                      </el-button>
                    </el-upload>
                  </div>
                </div>
                <div class="upload-preview">
                  <template v-if="imageUrl">
                    <el-image
                      :preview-src-list="[imageUrl]"
                      :src="imageUrl"
                      fit="cover"
                      style="width: 100%; height: 100%"
                    />
                    <span class="preview-close" @click.stop="handleRemoveImage">✕</span>
                  </template>
                  <span v-else>{{ $t('home.imagePreviewArea') }}</span>
                </div>
              </div>
            </el-form-item>
          </el-col>
        </el-row>
      </el-card>

      <!-- ========== 二、通知规则 ========== -->
      <el-card class="panel" shadow="never">
        <template slot="header">
          <h2 class="panel-title">{{ $t('home.notificationRulesSection') }}</h2>
        </template>

        <el-alert
          :closable="false"
          :title="$t('home.ruleDescription')"
          show-icon
          style="margin-bottom: 18px"
          type="info"
        />

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item :label="$t('home.system')" prop="systemType">
              <el-select
                v-model="form.systemType"
                style="width: 100%"
                @change="handleSystemTypeChange"
              >
                <el-option :label="$t('home.all')" :value="0" />
                <el-option :label="$t('home.expressSystem')" :value="1" />
                <el-option :label="$t('home.providerSystem')" :value="2" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('home.displayPosition')" prop="displayLocat">
              <el-checkbox-group v-model="form.displayLocat">
                <el-checkbox :label="1" border>{{ $t('home.loginPopup') }}</el-checkbox>
                <el-checkbox :label="2" border>{{ $t('home.importantReminderBanner') }}
                </el-checkbox>
                <el-checkbox :label="3" border>{{ $t('home.announcementCard') }}</el-checkbox>
              </el-checkbox-group>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 登录弹框规则（条件展示） -->
        <div v-if="form.displayLocat.length > 0" class="login-only-section">
          <div v-if="form.displayLocat.includes(1)" class="login-only-head">
            🔐 {{ $t('home.loginPopupRule') }}
          </div>
          <el-row v-if="form.displayLocat.includes(1)" :gutter="16">
            <el-col :span="12">
              <el-form-item :label="$t('home.userEnd')" prop="clientType">
                <el-select v-model="form.clientType" style="width: 100%">
                  <el-option :label="$t('home.all')" :value="0" />
                  <el-option :label="$t('home.pcEnd')" :value="1" />
                  <el-option :label="$t('home.appEnd')" :value="2" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item :label="$t('home.popupCloseMethod')" prop="howToClose">
                <el-select v-model="form.howToClose" style="width: 100%">
                  <el-option :label="$t('home.directlyClose')" :value="0" />
                  <el-option :label="$t('home.closeAfter10Seconds')" :value="10" />
                  <el-option :label="$t('home.closeAfter20Seconds')" :value="20" />
                  <el-option :label="$t('home.closeAfter30Seconds')" :value="30" />
                  <el-option :label="$t('home.closeAfter60Seconds')" :value="60" />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="16">
            <el-col :span="24">
              <el-form-item :label="$t('home.receiveUser')" prop="receptType">
                <div class="range-box">
                  <el-radio-group v-model="form.receptType" style="margin-bottom: 12px">
                    <el-radio :label="1" border>{{ $t('home.allUsers') }}</el-radio>
                    <el-radio :label="2" border>{{ $t('home.specifiedArea') }}</el-radio>
                    <el-radio :label="3" border>{{ $t('home.specifiedState') }}</el-radio>
                    <el-radio :label="4" border>{{ $t('home.specifiedSite') }}</el-radio>
                    <el-radio v-if="form.systemType !== 0" :label="5" border>
                      {{ $t('home.specifiedUser') }}
                    </el-radio>
                  </el-radio-group>
                  <div class="conditional-selects">
                    <!-- 指定大区：receptType === 2 -->
                    <el-form-item
                      v-if="form.receptType === 2"
                      :key="'regionId-' + form.receptType"
                      :rules="[
                        { required: true, message: $t('home.pleaseSelectArea'), trigger: 'change' }
                      ]"
                      prop="regionId"
                    >
                      <el-select
                        v-model="form.regionId"
                        :placeholder="$t('home.selectArea')"
                        clearable
                        filterable
                        style="width: 320px; margin-right: 12px"
                        @change="handleRegionChange"
                      >
                        <el-option
                          v-for="item in regionList"
                          :key="item.id"
                          :label="item.zhName + '（' + item.puName + '）'"
                          :value="item.id"
                        />
                      </el-select>
                    </el-form-item>
                    <!-- 指定州：receptType === 3 -->
                    <el-form-item
                      v-if="form.receptType === 3"
                      :key="'siteStateList-' + form.receptType"
                      :rules="[
                        {
                          required: true,
                          message: $t('home.pleaseSelectState2'),
                          trigger: 'change'
                        }
                      ]"
                      prop="siteStateList"
                    >
                      <el-select
                        v-model="form.siteStateList"
                        :placeholder="$t('home.selectState')"
                        clearable
                        collapse-tags
                        filterable
                        multiple
                        style="width: 320px"
                        @change="(val) => handleAllChange('siteStateList', val)"
                      >
                        <el-option
                          v-for="(item, index) in stateData"
                          :key="index"
                          :label="item.label"
                          :value="item.label"
                        />
                      </el-select>
                    </el-form-item>
                    <!-- 指定网点：receptType === 4 -->
                    <el-form-item
                      v-if="form.receptType === 4"
                      :key="'siteList-' + form.receptType"
                      :rules="[
                        { required: true, message: $t('home.pleaseSelectSite'), trigger: 'change' }
                      ]"
                      prop="siteList"
                    >
                      <el-select
                        v-model="form.siteList"
                        :placeholder="$t('home.selectSite')"
                        clearable
                        collapse-tags
                        filterable
                        multiple
                        style="width: 320px; margin-right: 12px"
                        @change="(val) => handleSelectChange('siteList', val, 20)"
                      >
                        <el-option
                          v-for="item in siteList"
                          :key="item.id"
                          :label="item.name"
                          :value="item.id"
                        />
                      </el-select>
                    </el-form-item>
                    <!-- 指定用户：receptType === 5 -->
                    <el-form-item
                      v-if="form.receptType === 5"
                      :key="'userList-' + form.receptType"
                      :rules="[
                        { required: true, message: $t('home.pleaseSelectUser'), trigger: 'change' }
                      ]"
                      prop="userList"
                    >
                      <el-select
                        v-model="form.userList"
                        :loading="userLoading"
                        :placeholder="$t('home.selectUserAccount')"
                        :remote-method="remoteSearchUser"
                        clearable
                        collapse-tags
                        filterable
                        multiple
                        remote
                        reserve-keyword
                        style="width: 320px"
                        @change="(val) => handleSelectChange('userList', val, 20)"
                        @clear="handleClearUser"
                      >
                        <el-option
                          v-for="item in userOptions"
                          :key="item.id"
                          :label="item.username"
                          :value="item.id"
                        />
                      </el-select>
                    </el-form-item>
                  </div>
                </div>
              </el-form-item>
            </el-col>
          </el-row>
        </div>
      </el-card>
      <!-- ========== 三、发布时间 ========== -->
      <el-card class="panel" shadow="never">
        <template slot="header">
          <h2 class="panel-title">{{ $t('home.publishTimeSection') }}</h2>
        </template>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item :label="$t('home.publishMethod')" prop="publicatFormat">
              <el-select v-model="form.publicatFormat" style="width: 100%">
                <el-option :label="$t('home.publishImmediately')" :value="1" />
                <el-option :label="$t('home.scheduledPublish')" :value="2" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('home.isTop')" prop="topType">
              <el-select v-model="form.topType" style="width: 100%">
                <el-option :label="$t('home.top')" :value="1" />
                <el-option :label="$t('home.notTop')" :value="0" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col v-if="form.publicatFormat === 2" :span="12">
            <el-form-item :label="$t('home.effectiveTime')" prop="effectiveTime">
              <el-date-picker
                v-model="form.effectiveTime"
                :picker-options="effectiveTimePickerOptions"
                :placeholder="$t('home.selectEffectiveTime')"
                format="yyyy-MM-dd HH:mm:ss"
                style="width: 100%"
                type="datetime"
                value-format="yyyy-MM-dd HH:mm:ss"
              />
            </el-form-item>
          </el-col>
          <el-col v-if="form.publicatFormat === 2" :span="12">
            <el-form-item :label="$t('home.expirationTime')" prop="failureTime">
              <el-date-picker
                v-model="form.failureTime"
                :placeholder="$t('home.selectExpirationTime')"
                format="yyyy-MM-dd HH:mm:ss"
                style="width: 100%"
                type="datetime"
                value-format="yyyy-MM-dd HH:mm:ss"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-card>
    </el-form>

    <!-- 底部操作栏 -->
    <div v-if="viewMode !== 'view'" class="footer">
      <el-button @click="handleCancel">{{ $t('trajectory.Cancel') }}</el-button>
      <el-button :loading="uploadLoading || draftLoading" @click="handleSaveDraft">{{ $t('home.saveDraft') }}
      </el-button>
      <el-button :loading="uploadLoading || submitLoading" type="primary" @click="handleSubmit">
        {{ $t('home.submitPublish') }}
      </el-button>
    </div>
  </div>
</template>

<script>
import { uploadOos } from '@/utils/ali_oos_util';
import { getSystemMessage, sendProviderMessage } from '@/api/newExpressAPI';
import { getLgAreaListData, getUserList, regionQueryList } from '@/api/orderApi';
import { getAllsite } from '@/api/logisticsOrders';
import Tinymce from '@/components/Tinymce/index.vue';

export default {
  name: 'AllocationRecords',
  components: {
    Tinymce
  },
  props: {
    msgId: {
      type: [String, Number],
      default: null
    },
    viewMode: {
      type: String,
      default: 'create'
    }
  },
  data() {
    return {
      userOptions: [],
      userLoading: false,
      uploadLoading: false,
      submitLoading: false,
      draftLoading: false,
      imageUrl: '',
      regionList: [],
      siteList: [],
      acceptType: '.jpg,.jpeg,.png,.JPG,.JPEG,.PNG',
      uploadAction: '#',
      msgPlugin: [''],
      toolbar: [''],
      isImage: false,
      form: {
        msgType: '',
        zhTitle: '',
        title: '',
        zhAbstract: '',
        puAbstract: '',
        zhText: '',
        puText: '',
        regionId: '',
        regionName: '',
        siteList: [],
        siteStateList: [],
        systemType: 0,
        displayLocat: [],
        clientType: 1,
        howToClose: 0,
        receptType: 1,
        userList: [],
        publicatFormat: 1,
        topType: 0,
        effectiveTime: '',
        failureTime: ''
      },
      initForm: {
        msgType: '',
        zhTitle: '',
        title: '',
        zhAbstract: '',
        puAbstract: '',
        zhText: '',
        puText: '',
        regionId: '',
        regionName: '',
        siteList: [],
        siteStateList: [],
        systemType: 0,
        displayLocat: [],
        clientType: 1,
        howToClose: 0,
        receptType: 1,
        userList: [],
        publicatFormat: 1,
        topType: 0,
        effectiveTime: '',
        failureTime: ''
      },
      rules: {
        msgType: [
          { required: true, message: this.$t('home.pleaseSelectMsgType'), trigger: 'change' }
        ],
        zhTitle: [
          { required: true, message: this.$t('home.pleaseInputChineseTitle'), trigger: 'blur' }
        ],
        title: [
          { required: true, message: this.$t('home.pleaseInputPortugueseTitle'), trigger: 'blur' }
        ],
        zhText: [
          { required: true, message: this.$t('home.pleaseInputChineseContent'), trigger: 'blur' }
        ],
        puText: [
          { required: true, message: this.$t('home.pleaseInputPortugueseContent'), trigger: 'blur' }
        ],
        systemType: [
          { required: true, message: this.$t('home.pleaseSelectSystem'), trigger: 'change' }
        ],
        displayLocat: [
          {
            type: 'array',
            required: true,
            message: this.$t('home.pleaseSelectDisplayPosition'),
            trigger: 'change',
            min: 1
          }
        ],
        publicatFormat: [
          { required: true, message: this.$t('home.pleaseSelectPublishMethod'), trigger: 'change' }
        ],
        topType: [
          { required: true, message: this.$t('home.pleaseSelectTopType'), trigger: 'change' }
        ],
        effectiveTime: [
          { required: true, message: this.$t('home.pleaseInput'), trigger: 'change' },
          {
            validator: (_rule, value, callback) => {
              if (this.form.publicatFormat === 2 && !value) {
                callback(new Error(this.$t('home.scheduledPublishNeedTime')));
              } else {
                callback();
              }
              if (this.form.failureTime) {
                this.$refs.formRef && this.$refs.formRef.validateField('failureTime');
              }
            },
            trigger: 'change'
          }
        ],
        failureTime: [
          {
            validator: (_rule, value, callback) => {
              if (!value) {
                callback();
                return;
              }
              if (
                this.form.effectiveTime &&
                new Date(value).getTime() <= new Date(this.form.effectiveTime).getTime()
              ) {
                callback(new Error(this.$t('home.failureTimeMustGreater')));
              } else {
                callback();
              }
            },
            trigger: 'change'
          }
        ],
        clientType: [
          {
            validator: (_rule, value, callback) => {
              if (this.form.displayLocat.includes(1) && (value === null || value === undefined)) {
                callback(new Error(this.$t('home.pleaseSelectUserEnd')));
              } else {
                callback();
              }
            },
            trigger: 'change'
          }
        ],
        howToClose: [{ required: true, message: this.$t('home.pleaseInput'), trigger: 'change' }]
      },
      stateData: [],
      effectiveTimePickerOptions: {
        disabledDate(time) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return time.getTime() < today.getTime();
        }
      }
    };
  },
  computed: {
    isEditMode() {
      return this.viewMode !== 'create';
    }
  },
  watch: {
    'form.receptType'(val) {
      if (val !== 2) {
        this.form.regionId = '';
        this.form.regionName = '';
        this.$refs.formRef && this.$refs.formRef.clearValidate('regionId');
      }
      if (val !== 3) {
        this.form.siteStateList = [];
        this.$refs.formRef && this.$refs.formRef.clearValidate('siteStateList');
      }
      if (val !== 4) {
        this.form.siteList = [];
        this.$refs.formRef && this.$refs.formRef.clearValidate('siteList');
      }
      if (val !== 5) {
        this.form.userList = [];
        this.$refs.formRef && this.$refs.formRef.clearValidate('userList');
      }
    },
    'form.systemType'(val) {
      if (val === 0) {
        if (this.form.receptType === 5) {
          this.form.receptType = 1;
          this.form.userList = [];
          this.$refs.formRef && this.$refs.formRef.clearValidate(['receptType', 'userList']);
        }
        this.userOptions = [];
      } else if (!this.isEditMode) {
        this.getUserList();
      }
    },
    'form.publicatFormat'(val) {
      if (val === 1) {
        this.form.effectiveTime = '';
        this.form.failureTime = '';
        this.$refs.formRef && this.$refs.formRef.clearValidate(['effectiveTime', 'failureTime']);
      }
    },
    'form.displayLocat'(val) {
      if (!val || !val.includes(1)) {
        this.form.clientType = 1;
        this.form.howToClose = 0;
        this.$refs.formRef && this.$refs.formRef.clearValidate(['clientType', 'howToClose']);
      }
    }
  },
  mounted() {
    this.getRegion();
    this.getAllSite();
    this.getState();
    if (this.msgId) {
      this.loadMessageDetail();
    }
  },
  methods: {
    handleRegionChange(val) {
      if (val) {
        const selectedRegion = this.regionList.find((item) => item.id === val);
        if (selectedRegion) {
          this.form.regionName = selectedRegion.zhName + '（' + selectedRegion.puName + '）';
        }
      } else {
        this.form.regionName = '';
      }
    },
    handleSystemTypeChange() {
      this.form.userList = [];
      this.getUserList();
      console.log(this.form.userList, 'this.form.userList');
    },
    getState() {
      const params = {
        level: 0,
        parentId: ''
      };
      getLgAreaListData(params, false).then((res) => {
        if (res.code === 200) {
          this.stateData = res.data.map((item) => {
            return {
              label: item.nameEn
            };
          });
        }
      });
    },
    async getRegion() {
      const res = await regionQueryList(3);
      if (res.code === 200) {
        this.regionList = res.data;
      }
    },
    getAllSite() {
      getAllsite().then((res) => {
        if (res.code === 200) {
          console.log('获取所有网点', res.data);
          this.siteList = res.data;
        }
      });
    },
    async getUserList(fuzzyFind = '') {
      this.userLoading = true;
      try {
        const data = {
          providerList: [],
          siteList: [],
          systemType: this.form.systemType,
          fuzzyFind: fuzzyFind
        };
        const res = await getUserList(data);
        if (res.code === 200) {
          const list = res.data.pmUsers || res.data.users || [];
          this.userOptions = list;
        }
      } finally {
        this.userLoading = false;
      }
    },
    remoteSearchUser(query) {
      if (query !== '') {
        this.getUserList(query);
      } else {
        this.userOptions = [];
      }
    },
    handleClearUser() {
      this.getUserList();
    },
    handleAllChange(field, val) {
      const list = val || [];
      const hasAll = list.includes(-1);
      if (hasAll) {
        if (list.length > 1) {
          this.form[field] = [-1];
        }
      }
    },
    handleSelectChange(field, val, max) {
      console.log(val, 'handleSelectChangehandleSelectChange');
      const list = val || [];
      const hasAll = list.includes(-1);
      if (hasAll) {
        if (list.length > 1) {
          this.form[field] = [-1];
        }
        return;
      }
      if (list.length > max) {
        this.form[field] = list.slice(0, max);
        this.$message.warning(this.$t('home.maxSelect20'));
      }
      if (list.length === 0) {
        this.getUserList();
      }
    },
    beforeUpload(file) {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        this.$message.error(this.$t('home.onlyImageAllowed'));
        return false;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        this.$message.error(this.$t('home.imageSizeLimit'));
        return false;
      }
      return true;
    },
    async customUpload(options) {
      this.uploadLoading = true;
      try {
        const res = await uploadOos(options.file, 'systemNotification');
        if (res.code === 200 && res.url) {
          this.imageUrl = res.url;
          this.$message.success(this.$t('home.uploadSuccess'));
        } else {
          this.$message.error(res.msg || this.$t('home.uploadFailed'));
        }
      } catch (error) {
        this.$message.error(this.$t('home.uploadFailed'));
      } finally {
        this.uploadLoading = false;
      }
    },
    handleRemoveImage() {
      this.imageUrl = '';
    },
    handleCancel() {
      Object.assign(this.form, this.initForm);
      this.imageUrl = '';
      this.$refs.formRef.resetFields();
    },
    handleBackList() {
      this.$emit('back');
    },
    async loadMessageDetail() {
      try {
        const res = await getSystemMessage({ messageId: this.msgId });
        if (res.code === 200) {
          const data = res.data || {};
          console.log(data, 'loadMessageDetail');
          this.fillForm(data);
        } else {
          this.$message.error(res.msg || this.$t('home.queryDetailFailed'));
        }
      } catch (error) {
        console.error('查询消息详情失败:', error);
        this.$message.error(this.$t('home.queryDetailFailedRetry'));
      }
    },
    fillForm(data) {
      const body = data.messageBody || {};
      let displayLocat = [];
      if (typeof body.displayLocat === 'string') {
        displayLocat = body.displayLocat
          .split(',')
          .filter(Boolean)
          .map((item) => Number(item));
      } else if (Array.isArray(body.displayLocat)) {
        displayLocat = body.displayLocat.map((item) => Number(item));
      }
      if (data.updateUserList && data.updateUserList.length > 0) {
        this.userOptions = data.updateUserList;
      } else {
        data.userList = [];
      }
      this.form = {
        ...this.form,
        msgType: body.msgType || '',
        zhTitle: body.zhTitle || '',
        title: body.title || '',
        zhAbstract: body.zhAbstract || '',
        puAbstract: body.puAbstract || '',
        zhText: body.zhText || '',
        puText: body.puText || '',
        systemType: data.systemType ?? 0,
        displayLocat,
        clientType: data.clientType ?? 1,
        howToClose: body.howToClose ?? 0,
        receptType: data.receptType ?? 1,
        regionId: (data.regionIdList && data.regionIdList[0]) || data.regionId || '',
        regionName: data.regionName || '',
        siteStateList: data.siteStateList || [],
        siteList: data.siteList || [],
        userList: data.userList || [],
        publicatFormat: body.publicatFormat ?? 1,
        topType: body.topType ?? 0,
        effectiveTime: body.effectiveTime || '',
        failureTime: body.failureTime || ''
      };
      this.imageUrl = body.textUrl || '';
    },
    async handleSaveDraft() {
      if (this.draftLoading) return;
      const valid = await this.$refs.formRef.validate().catch(() => false);
      if (!valid) return;

      this.draftLoading = true;
      try {
        const apiParams = this.transformFormToApi(this.form, 4);
        console.log('保存草稿参数:', apiParams);
        const res = await sendProviderMessage(apiParams);
        console.log('保存草稿成功:', res);
        this.$message.success(this.$t('home.saveDraftSuccess'));
        this.handleCancel();
        this.handleBackList();
      } catch (error) {
        console.error('保存草稿失败:', error);
        this.$message.error(this.$t('home.saveDraftFailed'));
      } finally {
        this.draftLoading = false;
      }
    },
    async handleSubmit() {
      const valid = await this.$refs.formRef.validate().catch(() => false);
      if (!valid) return;
      if (this.submitLoading) return;
      const imageDataRegex = /data:image/;
      if (imageDataRegex.test(this.form.zhText)) {
        this.$message.error(this.$t('GlobalSetObj.notInputImage'));
        return;
      }
      if (imageDataRegex.test(this.form.puText)) {
        this.$message.error(this.$t('GlobalSetObj.notInputImage'));
        return;
      }
      try {
        await this.$confirm(
          this.$t('home.submitPublishConfirm'),
          this.$t('home.submitPublishConfirmTitle'),
          {
            confirmButtonText: this.$t('home.confirmPublish'),
            cancelButtonText: this.$t('home.cancel'),
            type: 'warning'
          }
        );
      } catch (e) {
        return;
      }

      this.submitLoading = true;
      try {
        const targetStatus = this.form.publicatFormat === 2 ? 1 : 2;
        const apiParams = this.transformFormToApi(this.form, targetStatus);
        const res = await sendProviderMessage(apiParams);
        console.log('发布成功:', res);
        this.$message.success(this.$t('home.publishSuccess'));
        this.handleCancel();
        this.handleBackList();
      } catch (error) {
        console.error('发布失败:', error);
        this.$message.error(this.$t('home.publishFailed'));
      } finally {
        this.submitLoading = false;
      }
    },
    transformFormToApi(rawForm, status) {
      let effectiveTime = rawForm.effectiveTime;
      let failureTime = rawForm.failureTime;

      if (rawForm.publicatFormat === 1) {
        effectiveTime = null;
        failureTime = null;
      }
      const messageBody = {
        zhTitle: rawForm.zhTitle || '',
        title: rawForm.title || '',
        zhAbstract: rawForm.zhAbstract || '',
        puAbstract: rawForm.puAbstract || '',
        zhText: rawForm.zhText || '',
        puText: rawForm.puText || '',
        textUrl: this.imageUrl,
        displayLocat: (rawForm.displayLocat || []).filter(Boolean).join(','),
        publicatFormat: rawForm.publicatFormat,
        effectiveTime: effectiveTime,
        failureTime: failureTime,
        topType: rawForm.topType,
        msgType: rawForm.msgType,
        howToClose: rawForm.howToClose,
        dataSource: 2,
        status: status
      };
      if (this.viewMode === 'edit') {
        messageBody.messageId = this.msgId;
      }
      const params = {
        messageBody,
        receptType: rawForm.receptType,
        systemType: rawForm.systemType,
        clientType: rawForm.clientType
      };
      if (rawForm.receptType === 2) {
        params.regionId = rawForm.regionId;
        params.regionName = rawForm.regionName;
      }
      if (rawForm.receptType === 3) {
        params.siteStateList = rawForm.siteStateList;
      }
      if (rawForm.receptType === 4) {
        params.siteList = rawForm.siteList;
      }
      if (rawForm.receptType === 5) {
        params.userList = rawForm.userList;
      }
      return params;
    }
  }
};
</script>

<style scoped>
/* ===== 页面容器 ===== */
.page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  box-sizing: border-box;
}

/* ===== 标题行 ===== */
.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.title-row p {
  margin: 0;
  color: #667085;
  font-size: 14px;
}

/* ===== 面板/卡片 ===== */
.panel {
  border: 1px solid #e6ebf2;
  border-radius: 12px;
  margin-bottom: 10px;
  max-width: 1480px;
  flex-shrink: 0;
}

.panel >>> .el-card__header {
  border-bottom: 1px solid #edf1f6;
  padding: 16px 18px;
}

.panel >>> .el-card__body {
  padding: 18px;
}

.panel-title {
  margin: 0;
  font-size: 17px;
  color: #183b73;
}

/* ===== 表单标签加粗 ===== */
>>> .el-form-item__label {
  font-weight: 800;
  color: #344054;
}

/* ===== 上传区域 ===== */
.upload-area {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.upload-info {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  flex: 1;
}

.upload-icon {
  font-size: 32px;
  flex-shrink: 0;
}

.upload-text strong {
  font-size: 14px;
  color: #1f2937;
}

.upload-text p {
  margin: 4px 0 8px;
  font-size: 13px;
  color: #667085;
}

.upload-preview {
  width: 200px;
  height: 120px;
  border: 1px dashed #d0d5dd;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #98a2b3;
  font-size: 13px;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
}

.upload-preview .preview-close {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  font-size: 12px;
  color: #fff;
  background: #f56c6c;
  border-radius: 50%;
  cursor: pointer;
  z-index: 2;
}

.upload-btn {
  display: inline-block;
  margin-top: 4px;
}

/* ===== 登录弹框规则 ===== */
.login-only-section {
  border: 1px solid #e6ebf2;
  border-radius: 10px;
  padding: 16px;
  background: #fafbfc;
}

.login-only-head {
  font-size: 15px;
  font-weight: 800;
  color: #183b73;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid #edf1f6;
}

/* ===== 接收用户范围 ===== */
.range-box {
  width: 100%;
}

.conditional-selects {
  display: flex;
  margin-bottom: 8px;
}

.hint-text {
  margin: 8px 0 0;
  font-size: 13px;
  color: #667085;
}

/* ===== 底部操作栏 ===== */
.footer {
  position: sticky;
  bottom: 0;
  height: 56px;
  margin: 0 -10px;
  text-align: right;
  background: #fff;
  border-top: 1px solid #e8ecf3;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 0 24px;
  z-index: 10;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
}
</style>
